<template>
  <simple-dialog ref="dlg" title="New Miqaat" width="700" @ok="save">
    <template v-slot:content>
      <v-form>
        <v-text-field label="Miqaat Name" v-model="form.miqaatName" />
        <RichTextEditor label="Description" v-model="form.description" class="mb-4" />
        <v-row>
          <v-col cols="12" md="6">
            <VueDatePicker v-model="form.startDate" :enable-time-picker="false" auto-apply format="dd/MMM/yyyy" placeholder="Event start date" />
          </v-col>
          <v-col cols="12" md="6">
            <VueDatePicker v-model="form.endDate" :enable-time-picker="false" auto-apply format="dd/MMM/yyyy" placeholder="Event end date" />
          </v-col>
          <v-col cols="12" md="6">
            <VueDatePicker v-model="form.registrationStartDate" :enable-time-picker="false" auto-apply format="dd/MMM/yyyy" placeholder="Registration start" />
          </v-col>
          <v-col cols="12" md="6">
            <VueDatePicker v-model="form.registrationEndDate" :enable-time-picker="false" auto-apply format="dd/MMM/yyyy" placeholder="Registration end" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field label="Registration Commitment Amount" v-model.number="form.registrationCommitmentAmount" type="number" />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field label="Accommodation Amount" v-model.number="form.accommodationAmount" type="number" />
          </v-col>
        </v-row>
        <v-checkbox v-model="form.isActive" label="Active" density="compact" hide-details />
        <v-checkbox
          v-model="form.allowMemberSelfRegistration"
          label="Allow members to self-register"
          density="compact"
          hide-details
        />
        <v-checkbox
          :model-value="form.niazCommitmentRequired"
          label="Niaz commitment required"
          density="compact"
          hide-details
          @update:model-value="onRequiredChanged"
        />
        <v-checkbox
          :model-value="form.niazCommitmentCompulsory"
          label="Niaz commitment compulsory"
          density="compact"
          hide-details
          @update:model-value="onCompulsoryChanged"
        />
        <div class="d-flex ga-2 mt-3">
          <v-btn size="small" variant="outlined" class="mr-2" @click="openFilters">Filters ({{ activeFilterCount(filters) }})</v-btn>
          <v-btn size="small" variant="outlined" @click="openExclusions">Exclusions{{ hasExclusions(exclusions) ? ' (set)' : '' }}</v-btn>
        </div>
      </v-form>
    </template>
  </simple-dialog>

  <MiqaatFiltersDialog ref="dlgFilters" v-model="filters" />
  <MiqaatExclusionsDialog ref="dlgExclusions" v-model="exclusions" />
</template>

<script setup>
import { reactive, ref, inject } from "vue";
import { useRouter } from "vue-router";
import { createMiqaat } from "@/services/miqaatService";
import MiqaatFiltersDialog from "./MiqaatFiltersDialog.vue";
import MiqaatExclusionsDialog from "./MiqaatExclusionsDialog.vue";
import RichTextEditor from "@/components/RichTextEditor.vue";
import { emptySurveyFilters, filtersToRows, activeFilterCount } from "@/views/SurveyForms/utils/surveyFilterUtils";
import { emptySurveyExclusions, hasExclusions, exclusionsToPayload } from "@/views/SurveyForms/utils/surveyExclusionUtils";

const emit = defineEmits(["saved"]);

const router = useRouter();
const loader = inject("loader");
const snackbar = inject("snackBar");

const dlg = ref(null);
const dlgFilters = ref(null);
const dlgExclusions = ref(null);

const form = reactive({
  miqaatName: "",
  description: "",
  startDate: null,
  endDate: null,
  registrationStartDate: null,
  registrationEndDate: null,
  registrationCommitmentAmount: 0,
  accommodationAmount: 0,
  isActive: true,
  allowMemberSelfRegistration: true,
  niazCommitmentRequired: true,
  niazCommitmentCompulsory: true,
});

const filters = ref(emptySurveyFilters());
const exclusions = ref(emptySurveyExclusions());

function onRequiredChanged(value) {
  form.niazCommitmentRequired = value;
  if (!value) form.niazCommitmentCompulsory = false;
}

function onCompulsoryChanged(value) {
  form.niazCommitmentCompulsory = value;
  if (value) form.niazCommitmentRequired = true;
}

function resetForm() {
  Object.assign(form, {
    miqaatName: "",
    description: "",
    startDate: null,
    endDate: null,
    registrationStartDate: null,
    registrationEndDate: null,
    registrationCommitmentAmount: 0,
    accommodationAmount: 0,
    isActive: true,
    allowMemberSelfRegistration: true,
    niazCommitmentRequired: true,
    niazCommitmentCompulsory: true,
  });
  filters.value = emptySurveyFilters();
  exclusions.value = emptySurveyExclusions();
}

function show() {
  resetForm();
  dlg.value.show();
}

function openFilters() {
  dlgFilters.value.show();
}

function openExclusions() {
  dlgExclusions.value.show();
}

function buildPayload() {
  return {
    ...form,
    filters: filtersToRows(filters.value),
    ...exclusionsToPayload(exclusions.value),
  };
}

async function save() {
  if (!form.miqaatName?.trim()) {
    snackbar.error("Miqaat name is required");
    return;
  }
  if (!form.startDate || !form.endDate || !form.registrationStartDate || !form.registrationEndDate) {
    snackbar.error("All dates are required");
    return;
  }

  loader.show();
  try {
    const result = await createMiqaat(buildPayload());
    snackbar.success("Miqaat created");
    emit("saved");
    router.push(`/miqaat-management/${result.miqaatId}/edit`);
  } catch (error) {
    snackbar.error(error);
  } finally {
    loader.hide();
  }
}

defineExpose({ show });
</script>
